"use client";

import { Control } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";

import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";

interface NotificationStepProps {
  control: Control<any>;
}

export function NotificationStep({ control }: NotificationStepProps) {
  const { t } = useTranslation(["finances", "common"]);

  return (
    <div className="flex h-full flex-col px-6 py-8 max-sm:px-4 max-sm:py-6">
      {/* Header */}
      <div className="mb-8 text-center max-sm:mb-6">
        <h1 className="text-accent-foreground mb-3 flex items-center justify-center gap-3 text-2xl font-bold max-sm:text-xl">
          <Icon
            className="text-muted-foreground"
            height="1.75rem"
            icon="tabler:bell"
            width="1.75rem"
          />
          {t("finances:createPayment.steps.setupNotifications")}
        </h1>
        <p className="text-muted-foreground mx-auto max-w-md text-base max-sm:text-sm">
          {t("finances:createPayment.steps.notificationDesc")}
        </p>
      </div>

      {/* Content */}
      <div className="mx-auto w-full max-w-4xl flex-1 space-y-8">
        {/* Send Notification */}
        <div className="space-y-4">
          <FormField
            control={control}
            name="should_notify_payer"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-accent-foreground text-base font-medium">
                  {t("finances:createPayment.sendNotification")}
                </FormLabel>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    {
                      value: true,
                      label: t("finances:createPayment.yes"),
                      icon: "tabler:check",
                    },
                    {
                      value: false,
                      label: t("finances:createPayment.no"),
                      icon: "tabler:x",
                    },
                  ].map((option) => (
                    <motion.button
                      key={String(option.value)}
                      className={`relative overflow-hidden rounded-lg p-2 text-center ${
                        field.value === option.value
                          ? "bg-accent-foreground shadow-sm"
                          : "bg-muted/20 hover:bg-muted/30 hover:shadow-sm"
                      }`}
                      type="button"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => field.onChange(option.value)}
                    >
                      <div className="flex h-10 items-center gap-2">
                        <Icon
                          className={
                            field.value === option.value
                              ? "text-foreground"
                              : "text-muted-foreground"
                          }
                          height="1.25rem"
                          icon={option.icon}
                          width="1.25rem"
                        />
                        <span
                          className={`text-sm font-medium ${
                            field.value === option.value
                              ? "text-foreground"
                              : "text-accent-foreground"
                          }`}
                        >
                          {option.label}
                        </span>
                      </div>

                      {/* Selection indicator */}
                      {field.value === option.value && (
                        <motion.div
                          animate={{ scale: 1, opacity: 1 }}
                          className="absolute top-1/2 right-3 -translate-y-1/2"
                          initial={{ scale: 0, opacity: 0 }}
                        >
                          <div className="bg-muted/50 flex h-5 w-5 items-center justify-center rounded-full">
                            <Icon
                              className="text-foreground"
                              height="0.75rem"
                              icon="tabler:check"
                              width="0.75rem"
                            />
                          </div>
                        </motion.div>
                      )}
                    </motion.button>
                  ))}
                </div>
                <p className="text-muted-foreground text-sm">
                  {t("finances:createPayment.notificationHelper")}
                </p>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Payment Destination */}
        <div className="space-y-4">
          <FormField
            control={control}
            name="payment_destination"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-accent-foreground text-base font-medium">
                  {t("finances:createPayment.destination")}
                </FormLabel>
                <div className="bg-muted/30 rounded-xl p-2">
                  <FormControl>
                    <ScrollArea className="border-input ring-offset-background focus-within:ring-ring h-16 w-full rounded-xl bg-transparent">
                      <Textarea
                        className="[field-sizing:content] min-h-[64px] resize-none rounded-xl border-none! bg-transparent p-4 text-base break-words whitespace-pre-wrap focus-visible:ring-0 focus-visible:ring-offset-0"
                        maxLength={255}
                        placeholder={t("finances:createPayment.destinationPlaceholder")}
                        {...field}
                      />
                    </ScrollArea>
                  </FormControl>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-muted-foreground text-sm">
                    {t("finances:createPayment.destinationHelper")}
                  </p>
                  <div className="text-muted-foreground text-sm">
                    {field.value?.length || 0}/255
                  </div>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Custom Message */}
        <div className="space-y-4">
          <FormField
            control={control}
            name="notification_message"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-accent-foreground text-base font-medium">
                  {t("finances:createPayment.notificationMessage")}
                </FormLabel>
                <div className="bg-muted/30 rounded-xl p-2">
                  <FormControl>
                    <ScrollArea className="border-input ring-offset-background focus-within:ring-ring h-24 w-full rounded-xl bg-transparent">
                      <Textarea
                        className="[field-sizing:content] min-h-[96px] resize-none rounded-xl border-none! bg-transparent p-4 text-base break-words whitespace-pre-wrap focus-visible:ring-0 focus-visible:ring-offset-0"
                        maxLength={255}
                        placeholder={t("finances:createPayment.notificationMessagePlaceholder")}
                        {...field}
                      />
                    </ScrollArea>
                  </FormControl>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-muted-foreground text-sm">
                    {t("finances:createPayment.messageHelper")}
                  </p>
                  <div className="text-muted-foreground text-sm">
                    {field.value?.length || 0}/255
                  </div>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
    </div>
  );
}
